import { supabase } from '../lib/supabase';
import { subDays } from 'date-fns';

export const adminInboxService = {
  async getNotifications({ tab = 'all', category, severity, status, dateRange, searchTerm }) {
    const notifications = [];

    try {
      // Calculate date filter
      let dateFilter = null;
      if (dateRange && dateRange !== 'all') {
        const days = dateRange === 'today' ? 0 : parseInt(dateRange?.replace('days', ''));
        dateFilter = subDays(new Date(), days)?.toISOString();
      }

      // Fetch payment notifications
      if (tab === 'all' || tab === 'payments') {
        const { data: payments } = await supabase
          ?.from('payment_transactions')
          ?.select(`
            *,
            company_settings!inner(company_name)
          `)
          ?.in('payment_status', ['failed', 'pending', 'overdue'])
          ?.order('created_at', { ascending: false })
          ?.limit(50);

        payments?.forEach(payment => {
          notifications?.push({
            id: `payment-${payment?.id}`,
            category: 'payment',
            severity: payment?.payment_status === 'failed' ? 'critical' : 'high',
            title: `Payment ${payment?.payment_status}: ${payment?.company_settings?.company_name}`,
            message: `Transaction ${payment?.transaction_reference} - K${payment?.amount_kina?.toFixed(2)}`,
            amount: payment?.amount_kina,
            clientName: payment?.company_settings?.company_name,
            actionUrl: `/billing-subscription-management?transaction=${payment?.id}`,
            createdAt: payment?.created_at,
            readAt: null
          });
        });
      }

      // Fetch compliance alerts
      if (tab === 'all' || tab === 'compliance') {
        const { data: violations } = await supabase
          ?.from('compliance_violations')
          ?.select(`
            *,
            company_settings!inner(company_name)
          `)
          ?.in('violation_status', ['open', 'pending'])
          ?.order('identified_at', { ascending: false })
          ?.limit(50);

        violations?.forEach(violation => {
          notifications?.push({
            id: `compliance-${violation?.id}`,
            category: 'compliance',
            severity: violation?.severity,
            title: `${violation?.violation_type}: ${violation?.company_settings?.company_name}`,
            message: violation?.description,
            clientName: violation?.company_settings?.company_name,
            actionUrl: `/compliance-library?violation=${violation?.id}`,
            createdAt: violation?.identified_at || violation?.created_at,
            readAt: null
          });
        });
      }

      // Fetch support requests
      if (tab === 'all' || tab === 'support') {
        const { data: tickets } = await supabase
          ?.from('support_tickets')
          ?.select('*')
          ?.in('ticket_status', ['open', 'in_progress'])
          ?.order('created_at', { ascending: false })
          ?.limit(50);

        tickets?.forEach(ticket => {
          const severityMap = {
            'critical': 'critical',
            'high': 'urgent',
            'medium': 'medium',
            'low': 'low'
          };

          notifications?.push({
            id: `support-${ticket?.id}`,
            category: 'support',
            severity: severityMap?.[ticket?.priority] || 'medium',
            title: `Support Ticket #${ticket?.ticket_number}: ${ticket?.subject}`,
            message: ticket?.description,
            clientName: ticket?.client_name,
            actionUrl: `/support-ticket-management-center?ticket=${ticket?.id}`,
            createdAt: ticket?.created_at,
            readAt: null
          });
        });
      }

      // Fetch billing notifications
      if (tab === 'all' || tab === 'billing') {
        const { data: billingNotifications } = await supabase
          ?.from('billing_notifications')
          ?.select(`
            *,
            company_settings!inner(company_name)
          `)
          ?.eq('notification_status', 'pending')
          ?.order('created_at', { ascending: false })
          ?.limit(50);

        billingNotifications?.forEach(notification => {
          notifications?.push({
            id: `billing-${notification?.id}`,
            category: 'billing',
            severity: notification?.notification_type === 'overdue' ? 'urgent' : 'medium',
            title: `Billing: ${notification?.company_settings?.company_name}`,
            message: notification?.notification_message,
            clientName: notification?.company_settings?.company_name,
            actionUrl: `/billing-subscription-management?company=${notification?.company_id}`,
            createdAt: notification?.created_at,
            readAt: null
          });
        });
      }

      // Apply filters
      let filtered = notifications;

      if (category && category !== 'all') {
        filtered = filtered?.filter(n => n?.category === category);
      }

      if (severity && severity !== 'all') {
        filtered = filtered?.filter(n => n?.severity === severity);
      }

      if (searchTerm) {
        const term = searchTerm?.toLowerCase();
        filtered = filtered?.filter(n =>
          n?.title?.toLowerCase()?.includes(term) ||
          n?.message?.toLowerCase()?.includes(term) ||
          n?.clientName?.toLowerCase()?.includes(term)
        );
      }

      if (dateFilter) {
        filtered = filtered?.filter(n => new Date(n?.createdAt) >= new Date(dateFilter));
      }

      // Sort by date (newest first)
      filtered?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));

      return filtered;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  async getSummary() {
    try {
      const [payments, violations, tickets, billing] = await Promise?.all([
        supabase?.from('payment_transactions')?.select('id', { count: 'exact' })?.in('payment_status', ['failed', 'overdue']),
        supabase?.from('compliance_violations')?.select('id', { count: 'exact' })?.eq('severity', 'critical')?.in('violation_status', ['open', 'pending']),
        supabase?.from('support_tickets')?.select('id', { count: 'exact' })?.in('ticket_status', ['open', 'in_progress']),
        supabase?.from('billing_notifications')?.select('id', { count: 'exact' })?.eq('notification_status', 'pending')
      ]);

      const allNotifications = await this.getNotifications({ tab: 'all' });

      return {
        totalCount: allNotifications?.length || 0,
        paymentsCount: payments?.count || 0,
        complianceCount: violations?.count || 0,
        supportCount: tickets?.count || 0,
        billingCount: billing?.count || 0,
        urgentPayments: payments?.count || 0,
        criticalCompliance: violations?.count || 0,
        pendingSupport: tickets?.count || 0,
        billingAlerts: billing?.count || 0
      };
    } catch (error) {
      console.error('Error fetching summary:', error);
      return {
        totalCount: 0,
        paymentsCount: 0,
        complianceCount: 0,
        supportCount: 0,
        billingCount: 0,
        urgentPayments: 0,
        criticalCompliance: 0,
        pendingSupport: 0,
        billingAlerts: 0
      };
    }
  },

  subscribeToUpdates(callback) {
    const channels = [];

    // Subscribe to payment transactions
    const paymentChannel = supabase
      ?.channel('admin-inbox-payments')
      ?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'payment_transactions'
      }, (payload) => {
        if (payload?.new && ['failed', 'pending', 'overdue']?.includes(payload?.new?.payment_status)) {
          callback({
            id: `payment-${payload?.new?.id}`,
            category: 'payment',
            severity: payload?.new?.payment_status === 'failed' ? 'critical' : 'high',
            title: `Payment ${payload?.new?.payment_status}`,
            message: `Transaction ${payload?.new?.transaction_reference}`,
            createdAt: payload?.new?.created_at
          });
        }
      })
      ?.subscribe();

    channels?.push(paymentChannel);

    // Subscribe to compliance violations
    const complianceChannel = supabase
      ?.channel('admin-inbox-compliance')
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'compliance_violations'
      }, (payload) => {
        callback({
          id: `compliance-${payload?.new?.id}`,
          category: 'compliance',
          severity: payload?.new?.severity,
          title: `Compliance Violation: ${payload?.new?.violation_type}`,
          message: payload?.new?.description,
          createdAt: payload?.new?.identified_at || payload?.new?.created_at
        });
      })
      ?.subscribe();

    channels?.push(complianceChannel);

    // Subscribe to support tickets
    const supportChannel = supabase
      ?.channel('admin-inbox-support')
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'support_tickets'
      }, (payload) => {
        callback({
          id: `support-${payload?.new?.id}`,
          category: 'support',
          severity: 'medium',
          title: `New Support Ticket: ${payload?.new?.subject}`,
          message: payload?.new?.description,
          createdAt: payload?.new?.created_at
        });
      })
      ?.subscribe();

    channels?.push(supportChannel);

    return channels;
  },

  unsubscribe(channels) {
    if (Array?.isArray(channels)) {
      channels?.forEach(channel => {
        supabase?.removeChannel(channel);
      });
    }
  },

  async markAsRead(ids) {
    // This would update a read_status table if implemented
    // For now, this is a placeholder
    console.log('Marking as read:', ids);
    return true;
  },

  async archiveItems(ids) {
    // This would update an archive_status table if implemented
    // For now, this is a placeholder
    console.log('Archiving items:', ids);
    return true;
  },

  async exportReport(filters) {
    try {
      const notifications = await this.getNotifications(filters);
      
      // Convert to CSV
      const headers = ['Category', 'Severity', 'Title', 'Message', 'Client', 'Date'];
      const rows = notifications?.map(n => [
        n?.category,
        n?.severity,
        n?.title,
        n?.message,
        n?.clientName || 'N/A',
        new Date(n?.createdAt)?.toLocaleString()
      ]);

      const csv = [headers, ...rows]?.map(row => row?.join(','))?.join('\n');
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window?.URL?.createObjectURL(blob);
      const a = document?.createElement('a');
      a.href = url;
      a.download = `admin-inbox-report-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      a?.click();
      window?.URL?.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }
};

export default adminInboxService;